<template>
  <div class="inline-editor">
    <!-- The same fields (and validation) as this step of the wizard, edited right here instead of in a modal. -->
    <NewApplicationForm
      :key="formKey"
      ref="form"
      inline
      :section="step"
      :initial-data="candidate"
      :user-email="userEmail"
      :autosave="autosave"
      @dirty="onDirty"
      @live="$emit('live', $event)"
      @avatar-uploaded="$emit('avatar-uploaded', $event)"
      @email-changed="$emit('email-changed', $event)"
      @missing="missing = $event"
      @busy="busy = $event"
      @autosaved="onAutosaved"
      @autosave-blocked="$emit('autosave-blocked', $event)"
      @autosave-failed="$emit('autosave-failed', $event)"
    />

    <div v-if="barVisible" class="save-bar" :class="{ 'has-missing': missing > 0 }" role="region" aria-label="Guardar sección">
      <div class="save-bar-inner">
        <div class="save-bar-text" aria-live="polite">{{ barText }}</div>
        <div class="save-bar-step">{{ stepLabel }}</div>
        <div class="save-bar-actions">
          <button v-if="dirty" type="button" class="bar-ghost" :disabled="saving" @click="discard">Descartar</button>
          <button type="button" class="bar-solid" :disabled="saving || busy" @click="save">
            <PortalIcon name="save" :size="18" />
            <span>{{ saving ? "Guardando…" : saveLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NewApplicationForm from "../NewApplicationForm.vue";
import PortalIcon from "./PortalIcon.vue";
import { TAB_SECTIONS, sectionState } from "./candidateSummary";

const twoDigits = (number) => String(number).padStart(2, "0");

// A section of the portal edited in place: the wizard step's fields, always on screen. The first time, while the section
// is still to be completed, a white bar pinned to the bottom of the screen saves it; once completed, each change is
// saved by itself (autosave) and the portal says so in a notice.
export default {
  name: "InlineSectionEditor",
  components: { NewApplicationForm, PortalIcon },
  props: {
    // Wizard step index (0-2: the declaration, 3, is signed in its own modal).
    step: { type: Number, required: true },
    // The /candidate/me response the fields start from.
    candidate: { type: Object, required: true },
    userEmail: { type: String, default: "" },
  },
  data() {
    return {
      // Bumped to start the fields over from `candidate` (discarding, or once a save brings it back updated).
      formKey: 0,
      dirty: false,
      // Required fields still missing after a save attempt.
      missing: 0,
      // A file is still uploading.
      busy: false,
      saving: false,
      // The candidate an autosave brought back: the portal shows it without starting the fields over.
      autosavedCandidate: null,
    };
  },
  computed: {
    completing() {
      return sectionState(this.step, this.candidate) !== "done";
    },
    // A completed section saves each change by itself.
    autosave() {
      return !this.completing;
    },
    barVisible() {
      return this.completing;
    },
    saveLabel() {
      return this.completing ? "Guardar sección" : "Guardar cambios";
    },
    // "Paso 02 / 03": the position among the sections that have a card.
    stepLabel() {
      return `Paso ${twoDigits(this.step + 1)} / ${twoDigits(TAB_SECTIONS.length)}`;
    },
    barText() {
      if (this.missing > 0) {
        return this.missing === 1 ? "Falta 1 campo por completar" : `Faltan ${this.missing} campos por completar`;
      }
      if (this.dirty) return "Tiene cambios sin guardar";
      return "Complete y guarde para avanzar.";
    },
  },
  watch: {
    // The portal reloads the candidate after every save: the fields start over from what was stored. Not after an
    // autosave, which already shows what was saved (and the applicant may still be typing).
    candidate(value) {
      if (value && value === this.autosavedCandidate) return;
      this.restart();
    },
  },
  methods: {
    // Uploads a new profile photo into the form (from the credential's pencil); resolves once it is done, with whether it worked.
    changeAvatar(file) {
      return this.$refs.form ? this.$refs.form.uploadAvatar(file) : Promise.resolve(false);
    },
    onDirty(value) {
      this.dirty = value;
      this.$emit("dirty", value);
    },
    restart() {
      this.formKey += 1;
      this.missing = 0;
      this.busy = false;
      if (this.dirty) this.onDirty(false);
    },
    discard() {
      this.restart();
    },
    onAutosaved(event) {
      this.autosavedCandidate = event.candidate;
      this.$emit("autosaved", event);
    },
    // Saves now what an autosave has waiting (leaving the section). Resolves true when nothing is left unsaved; the
    // first time (a "Guardar sección" still to press), whether there was nothing to lose.
    flush() {
      if (this.autosave && this.$refs.form) return this.$refs.form.flushAutosave();
      return Promise.resolve(!this.dirty);
    },
    save() {
      if (this.saving || this.busy) return;
      this.saving = true;
      this.$refs.form
        .saveSection()
        .then((saved) => {
          if (saved) this.$emit("saved", this.step);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
/* White bar pinned to the bottom of the screen, as wide as the page; its content lines up with the page's. */
.save-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 50;
  border-top: 1px solid #eceef3;
  background: #fff;
  box-shadow: 0 -10px 30px -14px rgba(10, 16, 32, 0.2);
  -webkit-font-smoothing: antialiased;
}
.save-bar-inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-areas: "text step actions";
  align-items: center;
  gap: 8px 20px;
  max-width: 1600px;
  margin: 0 auto;
  padding: 14px var(--gutter, 24px);
  padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px));
}
.save-bar-text {
  grid-area: text;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font: 500 14px/1.4 "Geist", system-ui, sans-serif;
  color: #9a6a1c;
}
.save-bar-text::before {
  content: "";
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e6a23c;
}
.has-missing .save-bar-text {
  color: #c42b2b;
}
.has-missing .save-bar-text::before {
  background: #e5484d;
}
.save-bar-step {
  grid-area: step;
  font: 500 12px "Geist Mono", ui-monospace, monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a91a3;
  white-space: nowrap;
}
.save-bar-actions {
  grid-area: actions;
  display: flex;
  gap: 10px;
}
.bar-ghost,
.bar-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  font: 600 14px "Geist", system-ui, sans-serif;
  white-space: nowrap;
  cursor: pointer;
}
.bar-ghost {
  border: 1px solid #e1e4ea;
  background: #fff;
  color: #0e1424;
}
.bar-ghost:hover:not(:disabled) {
  background: #f6f7f9;
}
.bar-solid {
  border: 0;
  background: #0e1424;
  color: #fff;
}
.bar-solid:hover:not(:disabled) {
  background: #1a2338;
}
.bar-ghost:disabled,
.bar-solid:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* With the side column beside the main card, the bar's content spans just the main card (the bar itself stays full width). */
@media (min-width: 961px) {
  .save-bar-inner {
    padding-left: calc(var(--gutter, 24px) + var(--side-width, 340px) + var(--layout-gap, 24px));
  }
}

/* Phones: the status and the step on top, the buttons across the whole width below. */
@media (max-width: 640px) {
  .save-bar-inner {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "text step"
      "actions actions";
    gap: 10px 12px;
    padding-top: 12px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  }
  .save-bar-text {
    font-size: 13px;
  }
  .save-bar-step {
    font-size: 11px;
  }
  .save-bar-actions {
    width: 100%;
  }
  .bar-ghost,
  .bar-solid {
    height: 48px;
    border-radius: 14px;
    font-size: 15px;
  }
  .bar-solid {
    flex: 1;
  }
}
</style>
